import React, { ReactNode } from 'react';
import {
  createStyles,
  withStyles,
  Dialog as MUIDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
} from '@material-ui/core';
import { CloseOutlined } from '@material-ui/icons';
import SubmitButton from 'components/buttons/SubmitButton';

const styles = (theme) =>
  createStyles({
    closeButton: {
      position: 'absolute',
      minWidth: 0,
      right: theme.spacing(2.5),
      top: theme.spacing(3.25),
      color: theme.palette.secondary.contrastText,
    },

    title: {
      padding: theme.spacing(3),
    },

    content: {
      padding: theme.spacing(0, 4),
      minWidth: theme.spacing(50),
      minHeight: theme.spacing(8),
      overflowX: 'hidden',
    },

    error: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: theme.spacing(2, 4, 0),
      color: theme.palette.error.main,
    },

    fixError: {
      color: theme.palette.error.main,
      borderColor: theme.palette.error.main,

      '&:hover': {
        borderColor: theme.palette.error.dark,
      },
    },

    fixLink: {
      color: theme.palette.primary.main,
      cursor: 'pointer',

      '&:hover': {
        textDecoration: 'underline',
      },
    },

    actions: {
      padding: theme.spacing(4),

      '& .MuiButton-containedSecondary': {
        backgroundColor: theme.palette.secondary.light,

        '&:hover': {
          backgroundColor: theme.palette.primary.light,
        },
      },
    },

    note: {
      padding: theme.spacing(0, 4, 4),
      fontSize: 14,
      color: theme.palette.text.secondary,
      textAlign: 'center',
    },

    root: {},
  });

const Dialog = ({
  classes,
  open,
  title,
  confirmText = 'Ok',
  confirmDisabled = false,
  cancelText = 'Cancel',
  note = null,
  children,
  submitting,
  error,
  isErrorProcessedInContent,
  onCancel,
  onConfirm,
  onFixError,
  onFixRefillError,
  dialogProps,
  dialogContentProps,
  showActionBtns = true,
  showCancelButton = true,
  cancelSize = 6,
  confirmSize = 6,
  confirmUppercase = true,
  onClose,
  rolloPickup,
}: {
  classes: any;
  open: boolean;
  title: ReactNode;
  confirmText?: string;
  confirmDisabled?: boolean;
  cancelText?: string;
  note?: ReactNode;
  children: ReactNode;
  submitting?: boolean;
  error?: string;
  isErrorProcessedInContent?: boolean;
  onCancel: () => void;
  onConfirm?: (event) => void;
  onFixError?: () => void;
  onFixRefillError?: () => void;
  dialogProps?;
  dialogContentProps?;
  showActionBtns?;
  showCancelButton?: boolean;
  cancelSize?: number;
  confirmSize?: number;
  confirmUppercase?: boolean;
  onClose?: () => void;
  rolloPickup?: boolean;
}) => (
  <MUIDialog
    open={open}
    onClose={onClose || onCancel}
    {...dialogProps}
    classes={{ paper: classes.root }}>
    <Button className={classes.closeButton} onClick={onClose || onCancel}>
      <CloseOutlined />
    </Button>
    <DialogTitle className={classes.title}>{title}</DialogTitle>
    <DialogContent className={classes.content} {...dialogContentProps}>
      {children}
    </DialogContent>
    {error && !isErrorProcessedInContent && (
      <div className={classes.error}>
        {error}
        {(onFixError || onFixRefillError) && (
          <Button
            classes={{ root: classes.fixError }}
            variant="outlined"
            size="small"
            color="secondary"
            onClick={onFixError || onFixRefillError}>
            Fix it
          </Button>
        )}
      </div>
    )}
    {showActionBtns && (
      <DialogActions className={classes.actions}>
        <Grid container spacing={4}>
          {showCancelButton && (
            <Grid item xs={cancelSize || (onConfirm ? 6 : 12)}>
              <Button
                fullWidth
                variant={rolloPickup ? 'outlined' : 'contained'}
                color={onConfirm && !rolloPickup ? 'secondary' : 'primary'}
                size="large"
                style={{ textTransform: confirmUppercase ? 'uppercase' : 'none' }}
                onClick={onCancel}>
                {cancelText}
              </Button>
            </Grid>
          )}
          {onConfirm && (
            <Grid item xs={confirmSize}>
              <SubmitButton
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                label={confirmText}
                uppercase={confirmUppercase}
                disabled={submitting || confirmDisabled}
                submitting={submitting}
                onClick={onConfirm}
              />
            </Grid>
          )}
        </Grid>
      </DialogActions>
    )}
    {note && <div className={classes.note}>{note}</div>}
  </MUIDialog>
);

export default withStyles(styles)(Dialog);
